import { useRouterState } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ConfirmDialog } from '~/components/confirm-dialog'
import { SearchIcon } from '~/components/icons'
import { LogFoodPanel, type PanelDirt, type PanelWay } from '~/components/log-food-panel'
import { LOGGING_WAYS } from '~/components/logging-ways'
import { goalOn } from '~/data/nutrition'
import { useDayEntries, useNutrition } from '~/data/nutrition-store'
import { todayKey } from '~/data/weighins'
import { swallowNextClick } from '~/lib/swallow-click'

/**
 * The header's field on the food log, which is where food is found and logged — the nutrition
 * spec, Logging an entry. The field is one way of finding something and the glyphs along its
 * right are the others, and all of them answer in the one panel under it: what is typed
 * answers there, the camera looks through it, a quick entry is typed in it, and a food or a
 * meal opened out of any of them stands there too. Nothing about logging opens over the diary.
 *
 * It stands on every page under the food log, which is why it is rendered from the header and
 * not from the diary: a nutrient's page has no diary under it to hold what opens.
 */
export function FoodHeaderSearch() {
  const location = useRouterState({ select: (routerState) => routerState.location })
  // The day the diary is on, which is the day anything logged from here lands on.
  const askedDay = (location.search as { day?: string }).day
  const day = typeof askedDay === 'string' ? askedDay : todayKey()

  const data = useNutrition()
  const entries = useDayEntries(day) ?? []
  const goal = data === null ? null : goalOn(data.goals, day)

  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  /** Which way the panel is showing. The field is one of them, and the one it opens on. */
  const [way, setWay] = useState<PanelWay>('search')
  /** What the way holds that is worth asking about before it goes — see `LogFoodPanel`. */
  const [dirty, setDirty] = useState<PanelDirt | null>(null)
  const [asking, setAsking] = useState(false)
  const field = useRef<HTMLInputElement>(null)
  const holder = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  /** Away, and back to the field: what the panel held is finished with. */
  const close = useCallback(() => {
    setOpen(false)
    setWay('search')
    setTerm('')
    setDirty(null)
  }, [])

  /**
   * Asked to go. Where there is something half-typed in the panel it asks first — the panel is
   * where a quick entry is written and where the AI is asked, and a press somewhere else on
   * the page must not throw either away without a word.
   */
  const askToClose = useCallback(() => {
    if (dirty !== null) {
      setAsking(true)
      return
    }
    close()
  }, [dirty, close])

  // Closing on blur would beat the click to any control inside the panel — a press on a
  // result must land. So the panel closes on a pointer that goes down outside it.
  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (holder.current?.contains(target) === true) {
        return
      }
      /*
       * Anything the panel itself raised is the panel's, wherever it happens to be drawn: a
       * confirmation, the calendar a logging day is picked on, a menu. Every one of them is
       * drawn on the body rather than inside the panel, so without this a press on the day
       * you were choosing would be read as a press somewhere else and put the panel away.
       */
      if (
        target instanceof Element &&
        target.closest(
          '[aria-modal="true"],[data-radix-popper-content-wrapper],[role="dialog"],[role="menu"],[role="listbox"]',
        ) !== null
      ) {
        return
      }
      // Putting the panel away is the whole of what the press does — the click that follows
      // it would otherwise press whatever the panel was standing over.
      swallowNextClick()
      askToClose()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, askToClose])

  /**
   * The arrow keys walk the answers, the way they walk any list of suggestions under a field:
   * the row reached takes the focus, so it says so for eyes and for ears alike, and the enter
   * key takes it exactly as pressing it would. Up from the first row goes back to the field.
   */
  const walk = (step: 1 | -1) => {
    const rows = [...(panel.current?.querySelectorAll<HTMLElement>('[data-food-row]') ?? [])]
    if (rows.length === 0) {
      return
    }
    const at = rows.indexOf(document.activeElement as HTMLElement)
    const next = at === -1 ? (step === 1 ? 0 : rows.length - 1) : at + step
    if (next < 0) {
      field.current?.focus()
      return
    }
    rows[Math.min(next, rows.length - 1)]?.focus()
  }

  /*
   * The keys are taken on the panel rather than on the field alone, since the row reached
   * holds the focus: the next arrow key is pressed on a row, and must walk on from it rather
   * than do whatever a button does with an arrow key, which is nothing.
   */
  useEffect(() => {
    const element = holder.current
    if (element === null) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (way === 'search' && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault()
        setOpen(true)
        walk(event.key === 'ArrowDown' ? 1 : -1)
        return
      }
      if (event.key === 'Escape') {
        askToClose()
        field.current?.blur()
      }
    }
    element.addEventListener('keydown', onKeyDown)
    return () => {
      element.removeEventListener('keydown', onKeyDown)
    }
  })

  return (
    <>
      <div ref={holder} role="search" className="absolute inset-x-0 top-0 h-12">
        {/*
          Open, the field and what it answers with are one surface: the field at the top of a
          single rounded card, a rule under it, then the way that is showing. The card floats
          over the page from the field's own box, so the header does not grow.
        */}
        <div
          className={`absolute inset-x-0 top-0 flex max-h-[calc(100dvh-5rem)] flex-col overflow-hidden transition-colors ${
            open
              ? 'rounded-3xl bg-surface-overlay shadow-2xl ring-1 ring-white/10'
              : 'rounded-full bg-surface-raised'
          }`}
        >
          <div className="flex h-12 shrink-0 items-center gap-2 px-3 sm:gap-3 sm:pl-4">
            <SearchIcon className="h-6 w-6 shrink-0 text-ink-muted" />
            <input
              ref={field}
              type="search"
              aria-label="Search food"
              placeholder="Search food"
              value={term}
              onFocus={() => {
                setOpen(true)
                // The field is a way like the glyphs beside it: reaching for it chooses it.
                setWay((held) => (held === 'search' || dirty === null ? 'search' : held))
              }}
              onChange={(event) => {
                setTerm(event.target.value)
                setOpen(true)
                setWay('search')
              }}
              className="h-full w-full bg-transparent text-lg outline-none placeholder:text-ink-muted"
            />
            {/*
              The other ways, along the field's right: a glyph apiece and nothing else. Each is
              named for anything reading the page, and the one the panel is showing wears the
              accent — a name grown out beside a glyph under the pointer moves the field about
              every time the pointer crosses it, which is a field that will not hold still.
              Pressing the way already showing goes back to the field, which the panel opens on.
            */}
            <div className="flex shrink-0 items-center">
              {LOGGING_WAYS.map(({ way: which, label, Icon }) => (
                <button
                  key={which}
                  type="button"
                  aria-label={label}
                  aria-pressed={way === which}
                  className={`rounded-full p-2 transition-colors hover:bg-hover ${
                    way === which ? 'text-accent' : 'text-ink-muted hover:text-ink'
                  }`}
                  onClick={() => {
                    setOpen(true)
                    setWay((held) => (held === which ? 'search' : which))
                  }}
                >
                  <Icon aria-hidden className="size-6 shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {open && (
            <div
              ref={panel}
              /*
               * One height whatever the panel holds, so an answer arriving never moves what is
               * under the pointer — and tall enough for the camera to look through, which is
               * the tallest thing any of the ways puts here.
               */
              className="flex h-[min(34rem,calc(100dvh-7rem))] min-h-0 flex-col border-t border-white/10"
            >
              <LogFoodPanel
                day={day}
                entries={entries}
                goal={goal}
                data={data}
                way={way}
                typed={term}
                onDone={close}
                onDirty={setDirty}
              />
            </div>
          )}
        </div>
      </div>

      {asking && (
        <ConfirmDialog
          title={
            dirty === 'asked'
              ? 'Discard what you asked?'
              : dirty === 'plate'
                ? 'Discard this plate?'
                : 'Discard what you typed?'
          }
          body={
            dirty === 'plate' ? 'It has not been sent to anybody.' : 'Nothing has been logged yet.'
          }
          confirm="Discard"
          onConfirm={() => {
            setAsking(false)
            close()
          }}
          onDismiss={() => {
            setAsking(false)
          }}
        />
      )}
    </>
  )
}
